import React, { FC } from 'react';
import { renderToString } from 'react-dom/server';
import { sprintf } from 'sprintf';

import { Alert } from 'components/UI/Alert';

import { TxnState } from 'services/nfts';
import * as Types from 'types';

type Props = {
  txnState: TxnState;
  initialTxnState: TxnState;
  txnError?: Error | null;
  etherscanUrl: string | undefined;
  internalError?: Types.RenderableError<any>;
};

const renderLinkToUrl = (url: string | undefined, label: string) =>
  renderToString(
    <a target='_blank' className='link-border' href={url}>
      {label}
    </a>,
  );

export const FeedbackSection: FC<Props> = ({
  txnState,
  initialTxnState,
  txnError,
  etherscanUrl,
  internalError,
}) => {
  const getMessage = () => {
    switch (txnState) {
      case 'idle':
      case 'loading':
        if (internalError) {
          return internalError.message;
        }

        return sprintf(
          "You're about to transfer the ownership information of this edition from Sedition's servers onto the Ethereum blockchain. The ownership of this edition will be transferred from your account to the Ethereum wallet connected to your account. %s. Before proceeding please familiarize yourself with all the implications by carefully reading %s.",
          renderToString(<b>This action cannot be undone</b>),
          renderLinkToUrl(
            'https://www.seditionart.com/help-centre/nfts/beginner-s-nft-guide',
            'Our NFT guides',
          ),
        );
      case 'pending':
        return sprintf(
          'Sit back and relax! The transaction is now pending. You can check the transaction status via %s. On average, it rarely takes more than 5 minutes for a transaction to be completed. Come back to this page to view transaction status. Learn more in %s.',
          renderLinkToUrl(etherscanUrl, 'Etherscan'),
          renderLinkToUrl(
            'https://www.seditionart.com/help-centre/nfts/beginner-s-nft-guide',
            'Our NFT guides',
          ),
        );
      case 'error':
        return sprintf(
          'NFT creation failed. Failure reason: %s. For more information, visit %s.',
          txnError?.message || 'Unknown error occurred.',
          renderLinkToUrl(
            'https://www.seditionart.com/help-centre/nfts/sedition-nfts-in-a-nutshell',
            'Our NFT guides',
          ),
        );
      case 'reverted':
        return sprintf(
          'Blockchain transaction failed. You can check %s for more details. For more information, visit %s.',
          renderLinkToUrl(etherscanUrl, 'Etherscan'),
          renderLinkToUrl(
            'https://www.seditionart.com/help-centre/nfts/sedition-nfts-in-a-nutshell',
            'Our NFT guides',
          ),
        );
      case 'success':
        const wasMintedOnMount = initialTxnState === 'success';
        if (wasMintedOnMount) {
          return '';
        }

        return sprintf(
          'You have successfully transferred the certificate of ownership for this edition onto the Ethereum blockchain. Your artwork is now visible in your Ethereum wallet and it can be now transferred between wallets as well as traded on NFT marketplaces such as OpenSea. For more information about NFTs, visit %s',
          renderLinkToUrl('https://www.seditionart.com/help-centre/nfts', 'Our NFT guides'),
        );
    }
  };

  const getAlertType = () => {
    switch (txnState) {
      case 'idle':
      case 'loading':
        return 'warning';
      case 'error':
      case 'reverted':
        return 'error';
      default:
        return 'info';
    }
  };

  const message = getMessage();

  if (message.length === 0) {
    return null;
  }

  return (
    <div className='mb-1'>
      <Alert variant={getAlertType()} isClosable={false}>
        <p
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        />
      </Alert>
    </div>
  );
};
